import React from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';

import {Button, InfoDropdown, TActionButtonOption} from '@edna/components';
import {Icon, Stack} from '@edna/components/primitives';

import {ButtonContent} from 'src/components/primitives';
import {CoinsIcon} from 'src/icons';

import {useGetStripeAutopaymentInfoQuery, useRestoreStripeAutopaymentMutation} from '../../api';
import {EAutopaymentStatus} from '../../definitions';
import * as S from '../../style';
import {useStripeAutopaymentEnabled} from '../useAutopaymentEnabled';
import {showStripeSettingsModal} from './StripeSettingsModal';

const Content = React.memo<{action?: TEmptyFunction | null}>(({action}) => {
  const {t} = useTranslation();

  const handleChangeSettings = () => showStripeSettingsModal(true);

  return (
    <InfoDropdown
      overlayComponent={S.HintOverlay}
      head={
        <Button onClick={action || handleChangeSettings} size="S" appearance="secondary">
          <ButtonContent size="1">
            <Icon as={CoinsIcon} size="16px" />
            {t('BalanceReplenishment:actions.connectAutopayment')}
          </ButtonContent>
        </Button>
      }
      content={t('BalanceReplenishment:actions.connectAutopaymentHint')}
    />
  );
});

const StripeAutopaymentConnectButton = React.memo<{action?: TEmptyFunction | null}>(({action}) => {
  const {id: supportTenantId} = useParams<'id'>();

  const {data: info} = useGetStripeAutopaymentInfoQuery(supportTenantId);

  if (supportTenantId || info) {
    return null;
  }

  return <Content action={action} />;
});

const useStripeAutopaymentButtonOption = (): TActionButtonOption | null => {
  const {t} = useTranslation();

  const isAutopaymentEnabled = useStripeAutopaymentEnabled();

  const {data: info} = useGetStripeAutopaymentInfoQuery(undefined, {skip: !isAutopaymentEnabled});
  const [restoreAutopayment] = useRestoreStripeAutopaymentMutation();

  if (!isAutopaymentEnabled) {
    return null;
  }

  if (info?.customerStatus === EAutopaymentStatus.DISABLED) {
    return {
      content: (
        <Stack size="3" direction="ROW" align="center">
          <Icon as={CoinsIcon} size="20px" />
          {t('BalanceReplenishment:actions.restoreAutopayment')}
        </Stack>
      ),
      handler: () => restoreAutopayment(),
    };
  }

  return {
    content: (
      <Stack size="3" direction="ROW" align="center">
        <Icon as={CoinsIcon} size="20px" />
        {t(
          info
            ? 'BalanceReplenishment:actions.changeAutopaymentSettings'
            : 'BalanceReplenishment:actions.connectAutopayment',
        )}
      </Stack>
    ),
    handler: () => showStripeSettingsModal(!info),
  };
};

export {StripeAutopaymentConnectButton, useStripeAutopaymentButtonOption};
